import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Clock, Scissors, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function History() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/scans");
        setScans(data);
      } catch (err) {
        console.error("Failed to load history:", err);
      }
      setLoading(false);
    })();
  }, []);

  const savedPhotoCount = scans.filter((s) => s.storage_path).length;

  const handleDeleteAllPhotos = async () => {
    if (!window.confirm(
      `Delete every saved photo from your account (${savedPhotoCount} photo${savedPhotoCount === 1 ? "" : "s"})? Your text scan results will be kept.`
    )) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.delete("/scans/photos");
      setScans((prev) =>
        prev.map((s) =>
          s.storage_path
            ? { ...s, storage_path: undefined, photo_saved_by_user: false }
            : s
        )
      );
      toast.success(`Deleted ${data.deleted_count} photo${data.deleted_count === 1 ? "" : "s"} from your account.`);
    } catch (err) {
      toast.error("Couldn't delete photos. Try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeletePhoto = async (e, scanId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete the saved photo from your account? Your text results will be kept.")) {
      return;
    }
    setDeletingId(scanId);
    try {
      await api.delete(`/scans/${scanId}/photo`);
      setScans((prev) =>
        prev.map((s) =>
          s.id === scanId
            ? { ...s, storage_path: undefined, photo_saved_by_user: false }
            : s
        )
      );
      toast.success("Photo deleted from your account.");
    } catch (err) {
      toast.error("Couldn't delete photo. Try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="cm-label">SCAN HISTORY</p>
          <h1 className="cm-heading text-4xl sm:text-5xl mt-3">
            Every cut you&rsquo;ve <span className="text-copper">scanned</span>.
          </h1>
        </div>
        {savedPhotoCount > 0 && (
          <button
            type="button"
            onClick={handleDeleteAllPhotos}
            disabled={bulkDeleting}
            className="cm-btn-secondary inline-flex items-center gap-2 self-start md:self-auto"
            data-testid="history-delete-all-photos"
            aria-label={`Delete all ${savedPhotoCount} saved photos`}
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {bulkDeleting
              ? "Deleting…"
              : `Delete all saved photos (${savedPhotoCount})`}
          </button>
        )}
      </div>

      <div className="mt-10">
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : scans.length === 0 ? (
          <div className="cm-card p-10 text-center">
            <Clock className="w-7 h-7 text-gray-500 mx-auto" aria-hidden="true" />
            <p className="text-gray-400 mt-4">No scans yet.</p>
            <Link to="/scan" className="cm-btn-primary inline-flex mt-6">Run your first</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {scans.map((s) => (
              <div
                key={s.id}
                className="cm-card p-3 hover:-translate-y-1 hover:border-copper/50 transition-all relative group"
                data-testid={`history-scan-${s.id}`}
              >
                <Link to={`/scan/${s.id}`} className="block">
                  <div className="aspect-square overflow-hidden rounded-md bg-ink-800 flex items-center justify-center">
                    {s.storage_path ? (
                      <img
                        src={fileUrl(s.storage_path)}
                        alt={s.ai_result?.style_name || "scan"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center px-2">
                        <Scissors className="w-6 h-6 text-copper mx-auto" aria-hidden="true" />
                        <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-2">
                          Photo purged
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="mt-3 text-sm font-semibold truncate">
                    {s.ai_result?.style_name || (s.ai_result?.error ? "Unrecognized" : "Untitled")}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                    {new Date(s.created_at).toLocaleString()}
                  </p>
                </Link>
                {s.storage_path && (
                  <button
                    type="button"
                    onClick={(e) => handleDeletePhoto(e, s.id)}
                    disabled={deletingId === s.id}
                    aria-label="Delete saved photo"
                    title="Delete saved photo"
                    className="absolute top-2 right-2 w-8 h-8 inline-flex items-center justify-center rounded-md bg-ink-950/80 backdrop-blur border border-ink-700 text-platinum hover:border-red-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                    data-testid={`history-delete-photo-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
